import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { APP_CONSTANTS, ROUTES } from '@/app/config/constants';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { logoutStart, selectUser, useFeatureAccess, useSessionWatch } from '@/features/authentication';
import { RFQ_FEATURE } from '@/features/bids';
import { getInitial } from '@/shared/utils';
import '../layout.css';
import '../supplierLayout.css';
/* The reference's icons for the shell too: sidebar, breadcrumb and user menu (2026-10-27). */
import '../supplierIcons.css';
import { useSidebar } from '../hooks/useSidebar';
import { HeaderNotifications } from './header/HeaderNotifications';
import { SupplierCrumbContext } from '../supplierCrumb';
import { SidebarFooter } from './sidebar/SidebarFooter';

/**
 * The header's trail, as the reference reads it: BIDs, then the BID, then Submit Bid on its quotation -- or Dashboard
 * on the supplier's dashboard (2026-10-29, NB-637).
 */
function SupplierCrumbs({ pathname, rfqNumber }: { pathname: string; rfqNumber: string | null }) {
  const match = /^\/supplier-bids\/(\d+)(\/quotation)?/.exec(pathname);
  const trail: { label: string; to?: string }[] = isDashboard(pathname)
    ? [{ label: 'Dashboard' }]
    : [{ label: 'BIDs', to: ROUTES.SUPPLIER_BIDS }];
  if (match?.[1]) {
    trail.push({ label: rfqNumber ?? 'BID', to: `${ROUTES.SUPPLIER_BIDS}/${match[1]}` });
    if (match[2]) trail.push({ label: 'Submit Bid' });
  }
  return (
    <nav aria-label="Breadcrumb" style={{ minWidth: 0 }}>
      <ol className="supplier-crumbs">
        {trail.map((crumb, index) => {
          const last = index === trail.length - 1;
          return (
            <li key={`${crumb.label}-${String(index)}`} className="supplier-crumbs__item">
              {index > 0 ? <i className="ti ti-chevron-right supplier-crumbs__sep" aria-hidden="true" /> : null}
              {last || !crumb.to ? (
                <span className="supplier-crumbs__here" aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <NavLink className="supplier-crumbs__link" to={crumb.to} end>
                  {crumb.label}
                </NavLink>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * The supplier's account menu, as the staff header's (the reference draws one header for every role): avatar, name and
 * role with a chevron, opening onto who is signed in and Logout -- a supplier has no profile, settings or password.
 */
function SupplierUserMenu({ name, email, onSignOut }: { name: string; email: string; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="header__user-menu" ref={wrapper}>
      <button
        type="button"
        className="header__user-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="supplier-user-menu"
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <span className="header__avatar">{getInitial(name)}</span>
        <span className="header__user-meta">
          <span className="header__user-name">{name}</span>
          <span className="header__user-role">Supplier</span>
        </span>
        <i
          className={`ti ti-chevron-down header__user-chevron${open ? ' header__user-chevron--open' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div className="header__dropdown" id="supplier-user-menu" role="menu">
          <div className="header__dropdown-header">
            <p className="header__dropdown-name">{name}</p>
            <p className="header__dropdown-email">{email}</p>
          </div>
          <div className="header__dropdown-divider" />
          <button
            type="button"
            role="menuitem"
            className="header__dropdown-item header__dropdown-item--action"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            <i className="ti ti-logout header__dropdown-item-icon" aria-hidden="true" />
            <span>Logout</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The supplier's dashboard (2026-10-29, NB-637). */
function isDashboard(pathname: string): boolean {
  return pathname === ROUTES.SUPPLIER_DASHBOARD || pathname.startsWith(`${ROUTES.SUPPLIER_DASHBOARD}/`);
}

/** The supplier shell's page titles: its dashboard, its BIDs, and one BID's quotation. */
function supplierPageTitle(pathname: string): string {
  if (isDashboard(pathname)) return 'Dashboard';
  return pathname === ROUTES.SUPPLIER_BIDS || pathname === `/` ? 'BIDs' : pathname.endsWith('/quotation') ? 'BID Quotation' : 'BID Details';
}

/**
 * The shell a supplier works in -- the Supplier Bid Quotation portal (2026-10-23).
 *
 * A supplier signs in with a one-time code and holds no user account, so the staff shell's own reads -- the menu, the
 * entity list, notifications, the entity's formats, the organization's brand -- have nothing to answer them with. This
 * shell is the staff one's look (its classes and footer) with only what a supplier has: its BIDs, who it is, and Sign
 * out. It holds a supplier to its BID pages whatever URL is typed, and checks the session as the staff shell does.
 */
export function SupplierLayout() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [crumb, setCrumb] = useState<string | null>(null);
  const { pathname } = useLocation();
  /*
    BIDs by RBAC (2026-10-27): the supplier's BIDs follow the Supplier role's BIDs permission in RBAC → Functions.
    Without it there is no BIDs entry and no BID page -- the shell says so rather than bouncing to a page outside it.
  */
  const { canReach } = useFeatureAccess();
  const hasBids = canReach(RFQ_FEATURE);
  /* Signed out within a minute when the session ends elsewhere, as in the staff shell. */
  useSessionWatch();

  useEffect(() => {
    document.title = `${supplierPageTitle(pathname)} · ${APP_CONSTANTS.APP_NAME}`;
  }, [pathname]);

  /* Only the dashboard (2026-10-29, NB-637) and the BID pages are a supplier's; "/" is its dashboard. */
  if (!pathname.startsWith(ROUTES.SUPPLIER_BIDS) && !isDashboard(pathname)) {
    return <Navigate to={pathname === ROUTES.ROOT ? ROUTES.SUPPLIER_DASHBOARD : ROUTES.SUPPLIER_BIDS} replace />;
  }

  const name = user?.name ?? '';

  return (
    <div className="app-layout__root supplier-shell">
      <a className="app-layout__skip-link" href="#main-content">
        Skip to main content
      </a>

      <aside id="app-sidebar" className={`sidebar${isCollapsed ? ' sidebar--collapsed' : ''}`}>
        <div className="sidebar__org">
          <span className="sidebar__org-left">
            <span className="sidebar__org-logo-placeholder" aria-hidden="true">
              {getInitial(name)}
            </span>
            {!isCollapsed ? (
              <span className="sidebar__org-name" title={name}>
                {name}
              </span>
            ) : null}
          </span>
        </div>

        <nav className="sidebar__nav" aria-label="Main navigation">
          <ul className="sidebar__list">
            {hasBids ? (
              <li className="sidebar__item">
                <NavLink
                  to={ROUTES.SUPPLIER_DASHBOARD}
                  data-pr-tooltip={isCollapsed ? 'Dashboard' : undefined}
                  className={({ isActive }) => (isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link')}
                >
                  <i className="ti ti-layout-grid sidebar__icon" aria-hidden="true" />
                  <span className="sidebar__label">Dashboard</span>
                </NavLink>
              </li>
            ) : null}
            {hasBids ? (
              <li className="sidebar__item">
                <NavLink
                  to={ROUTES.SUPPLIER_BIDS}
                  data-pr-tooltip={isCollapsed ? 'BIDs' : undefined}
                  className={({ isActive }) => (isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link')}
                >
                  <i className="ti ti-file-description sidebar__icon" aria-hidden="true" />
                  <span className="sidebar__label">BIDs</span>
                </NavLink>
              </li>
            ) : null}
          </ul>
        </nav>

        <SidebarFooter isCollapsed={isCollapsed} />
      </aside>

      <div className="app-layout__main-col">
        <header className="app-header" aria-label="Application header">
          <div className="header__left">
            <button
              type="button"
              className="header__toggle"
              onClick={toggleSidebar}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-controls="app-sidebar"
              aria-expanded={!isCollapsed}
            >
              <svg
                className="header__toggle-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 3v18" />
                <path d={isCollapsed ? 'm14 9 3 3-3 3' : 'm16 15-3-3 3-3'} />
              </svg>
            </button>
            <h1 className="visually-hidden">{supplierPageTitle(pathname)}</h1>
            <SupplierCrumbs pathname={pathname} rfqNumber={crumb} />
          </div>

          <div className="header__right">
            {/*
              The same bell the staff header carries, and the same component: a supplier's notifications are
              the same rows, scoped by the server to their own supplier account rather than to a user id.
              `enabled` is unconditional here because reaching this layout at all means a supplier session.
            */}
            <HeaderNotifications enabled />
            <SupplierUserMenu
              name={name}
              email={user?.email ?? ''}
              onSignOut={() => {
                dispatch(logoutStart());
              }}
            />
          </div>
        </header>
        <main className="app-layout__main" id="main-content">
          {hasBids ? (
            <SupplierCrumbContext.Provider value={setCrumb}>
              <Outlet />
            </SupplierCrumbContext.Provider>
          ) : (
            <section className="supplier-noaccess" role="alert">
              <i className="ti ti-lock supplier-noaccess__icon" aria-hidden="true" />
              <h2 className="supplier-noaccess__title">No access to BIDs</h2>
              <p className="supplier-noaccess__text">
                Your account does not have permission to view BIDs. Please contact the buyer&rsquo;s administrator.
              </p>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
